import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  ApexAxisChartSeries,
  ApexChart,
  ChartComponent,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexAnnotations,
  ApexFill,
  ApexStroke,
  ApexGrid,
  ApexXAxis,
  ApexTitleSubtitle
} from "ng-apexcharts";
import { Observable, Subject, takeUntil } from 'rxjs';
import { SharedService } from 'src/app/services/shared.service';
import { AppState } from 'src/app/store/app.state';
import { getDashboardCounts, getUserProfile } from 'src/app/store/selectors/account.selector';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: any;
  annotations: ApexAnnotations;
  fill: ApexFill;
  stroke: ApexStroke;
  grid: ApexGrid;
};

export type lastTwoMonthsChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
};


@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent implements OnInit {

  getDashboardCounts$: Observable<any>;
  dashboardCounts: any

  @ViewChild("chart") chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;

  @ViewChild("lastTwoMonthschart") lastTwoMonthsChart: ChartComponent;
  public lastTwoMonthsChartOptions: Partial<lastTwoMonthsChartOptions>;

  ngUnsubscribe = new Subject<void>();

  getUserProfile$: Observable<any>;
  userProfile: any;
  constructor(
    private store: Store<AppState>,
    private sidenavService: SharedService
  ) {
    this.getDashboardCounts$ = this.store.select(getDashboardCounts);
    this.getUserProfile$ = this.store.select(getUserProfile);
  }

  ngOnInit() {
    this.getUserProfile$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((res) => {
      if (res) {
        this.userProfile = res;
      }
    });
    this.getDashboardCounts$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((res) => {
      if (res) {
        this.dashboardCounts = res
        this.initializeLast2MonthsChart();
        this.initializeRegionalChart();
      }
    })

    this.sidenavService.sidenavToggle$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 200);
      });
  }

  getFormattedRegionLabels(regionLabels: string[]): string[] {
    return regionLabels.map(label => {
      const upperLabel = label.toUpperCase();

      if (upperLabel.startsWith("REGION")) {
        const match = label.match(/^(Region\s+[IVXLCDM0-9]+(?:-[A-Z])?)/i);
        return match ? match[1] : label;
      }

      const parenMatch = label.match(/\(([^)]+)\)/);
      if (parenMatch) return parenMatch[1];

      if (/\s+Region$/i.test(label)) {
        return label.replace(/\s+Region$/i, "").trim();
      }

      return label;
    });
  }

  initializeRegionalChart() {
    var regions = this.dashboardCounts?.allTime?.map((e) => e?.region);
    var data = this.dashboardCounts?.allTime?.map((e) => e?.count);

    this.chartOptions = {
      series: [
        {
          name: "Total",
          data: data || []
        }
      ],
      chart: {
        id: "region-bar-chart",
        height: 350,
        type: "bar",
        zoom: { enabled: false },
        toolbar: { show: false }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: 2
      },
      grid: {
        row: {
          colors: ["#fff", "#f2f2f2"]
        }
      },
      xaxis: {
        labels: {
          style: {
            fontSize: "10px",
            fontFamily: "Helvetica, Arial, sans-serif",
            fontWeight: 400,
            colors: ["#000"]
          },
          rotate: -45,
          rotateAlways: true
        },
        categories: this.getFormattedRegionLabels(regions) || [],
        tickPlacement: "on"
      },
      yaxis: {
        // title: {
        //   text: "PWD Entries"
        // }
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "horizontal",
          shadeIntensity: 0.25,
          gradientToColors: undefined,
          inverseColors: true,
          opacityFrom: 0.85,
          opacityTo: 0.85,
          stops: [50, 0, 100]
        }
      }
    };
  }

  initializeLast2MonthsChart() {
    const originalData = (this.dashboardCounts?.last2Months || []).map(item => ({
      date: new Date(item.date),
      count: item.count
    })).sort((a, b) => a.date.getTime() - b.date.getTime());

    if (!originalData.length) return;

    const startDate = new Date(originalData[0].date);
    const endDate = new Date();

    const allDates: string[] = [];
    const dateCounts: Record<string, number> = {};

    originalData.forEach(item => {
      const key = item.date.toISOString().split("T")[0];
      dateCounts[key] = item.count;
    });

    const seriesData: number[] = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().split("T")[0];
      allDates.push(d.toLocaleDateString("en-US", { month: "short", day: "2-digit" }));
      seriesData.push(dateCounts[key] ?? 0);
    }

    const defaultDays = 30;
    const totalPoints = allDates.length;
    const endIndex = totalPoints - 1;
    const startIndex = Math.max(0, endIndex - defaultDays + 1);

    this.lastTwoMonthsChartOptions = {
      series: [
        {
          name: this.userProfile?.agn_code == 'PSB' ? 'PSBs' : "PWDs",
          data: seriesData
        }
      ],
      chart: {
        height: 350,
        type: "line",
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: true
        },
        toolbar: {
          show: true,
          tools: {
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "straight"
      },
      grid: {
        row: {
          colors: ["#f3f3f3", "transparent"],
          opacity: 0.5
        }
      },
      xaxis: {
        labels: {
          style: {
            fontSize: "10px",
            fontFamily: "Helvetica, Arial, sans-serif",
            fontWeight: 400,
            colors: ["#000"]
          },
          rotate: -45,
          rotateAlways: true
        },
        categories: allDates,
        min: startIndex,
        max: endIndex
      }
    };
  }
}
